/**
 * A Recents row's hover card: the full title, its space, model and status,
 * when it was last active, and its last error.
 *
 * @module components/home/RecentThreadHoverCard
 */
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import { CircleAlertIcon, ClockIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import type { deriveProviderInstanceEntries } from "../../providerInstances";
import type { Space } from "../../state/spaces";
import { ProviderInstanceIcon } from "../chat/ProviderInstanceIcon";
import { getTriggerDisplayModelLabel } from "../chat/providerIconUtils";
import { SpaceGlyph } from "../spaces/SpaceGlyph";
import { TooltipPopup } from "../ui/tooltip";
import { compactTime, type RecentsSection } from "./HomeSidebarRecents.logic";
import type { RecentStatusMeta } from "./recentThreadStatus";

export function RecentThreadHoverCard({
  thread,
  section,
  space,
  providerEntry,
  status,
}: {
  readonly thread: EnvironmentThreadShell;
  readonly section: RecentsSection;
  readonly space: Space | null;
  readonly providerEntry: ReturnType<typeof deriveProviderInstanceEntries>[number] | null;
  readonly status: RecentStatusMeta | null;
}) {
  const selectedModel = providerEntry?.models.find((model) => model.slug === thread.modelSelection.model);
  const modelLabel = selectedModel ? getTriggerDisplayModelLabel(selectedModel) : thread.modelSelection.model;
  return (
    <TooltipPopup
      side="right"
      align="start"
      sideOffset={8}
      variant="glass"
      className="max-w-80 text-left whitespace-normal [&_[data-slot=tooltip-viewport]]:p-0"
    >
      <div className="flex min-w-0 max-w-80 flex-col gap-2 p-[var(--floating-content-inset)]">
        <div className="min-w-0 text-xs leading-tight font-medium text-foreground">{thread.title}</div>
        <div className="grid gap-1.5 pl-0.5 text-xs text-muted-foreground">
          {space ? (
            <div className="flex min-w-0 items-center gap-2">
              <SpaceGlyph space={space} className="size-3 rounded-[3px] text-[7px] leading-none" />
              <div className="min-w-0 truncate text-foreground/75">{space.name}</div>
            </div>
          ) : null}
          {providerEntry ? (
            <div className="flex min-w-0 items-center gap-2">
              <ProviderInstanceIcon
                driverKind={providerEntry.driverKind}
                displayName={providerEntry.displayName}
                iconClassName="size-3 shrink-0 grayscale opacity-60"
              />
              <div className="min-w-0 truncate text-foreground/75">{modelLabel}</div>
            </div>
          ) : null}
          {status ? (
            <div className={cn("flex min-w-0 items-center gap-2", status.className)}>
              <status.Icon className="size-3 shrink-0" />
              <div className="min-w-0 truncate">{status.label}</div>
            </div>
          ) : null}
          <div className="flex min-w-0 items-center gap-2">
            <ClockIcon className="size-3 shrink-0" />
            <div className="min-w-0 truncate text-foreground/75">
              Last activity {compactTime(thread.latestUserMessageAt ?? thread.updatedAt)}
              {section === "settled" ? " · settled" : section === "snoozed" ? " · snoozed" : ""}
            </div>
          </div>
          {thread.session?.lastError ? (
            <div className="flex min-w-0 items-start gap-2 text-error-foreground">
              <CircleAlertIcon className="mt-0.5 size-3 shrink-0" />
              <div className="min-w-0 line-clamp-3">{thread.session.lastError}</div>
            </div>
          ) : null}
        </div>
      </div>
    </TooltipPopup>
  );
}
